import { makeAutoObservable } from "mobx";
import { cloudStorage } from "@/utils/storage";
import { PageLoadManager } from "@/utils/tool";
import { localCacheKeyMap } from "./constant";
import { getCloudProjects as requestCloudProjects } from "./request";
import { CloudProjectType, CurrentCloudProject } from "./types/cloud";

class CloudProjectsManager {
  list: CloudProjectType[] = [];
  currentProject?: CurrentCloudProject;
  listManager: PageLoadManager<CloudProjectType[]>;
  saving = false;
  lastSaveTime: null | Date | number = null;
  conflictModalOpen = false;

  get info() {
    return this;
  }

  get total() {
    return this.listManager.total;
  }

  get curPage() {
    return this.listManager.pageIdx;
  }

  fetchListHandler = async (idx: number, size: number) => {
    const res = await requestCloudProjects(
      idx,
      size,
      cloudStorage.get(localCacheKeyMap.commit_id),
    ).catch((err) => console.error("requestCloudProjects err", err));
    if (res) {
      return {
        total: res.data?.total ?? 0,
        data: res.data?.projects ?? [],
      };
    }
    return {
      total: 0,
      data: [],
    };
  };

  constructor() {
    this.listManager = new PageLoadManager(this.fetchListHandler, 12);
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setConflictModalOpen = (open: boolean) => {
    this.conflictModalOpen = open;
  };

  setSaving(isSaving: boolean) {
    this.saving = isSaving;
  }

  setLastSaveTime(lastSaveTime: null | Date | number) {
    this.lastSaveTime = lastSaveTime;
  }

  setCurrentProject = (project: CurrentCloudProject) => {
    this.currentProject = project;
  };

  updateCurrentProject = <T extends keyof CurrentCloudProject>(
    key: T,
    value: CurrentCloudProject[T],
  ) => {
    if (!this.currentProject) return;
    this.currentProject[key] = value;
  };

  getCloudProjects = async (increase?: boolean) => {
    const handler = increase ? this.listManager.next : this.listManager.fetch;
    this.list = await handler();
    return {
      list: this.list,
      total: this.listManager.total,
    };
  };

  refreshCloudProjects = async () => {
    this.list = await this.listManager.refreshAll();
    return this.list;
  };

  deleteCloudProject = (projectId: string) => {
    this.list = this.list.filter((project) => project.project_id !== projectId);
  };
}

export const cloudProjectsManager = new CloudProjectsManager();
